import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { setVaultSyncEnabled } from '../../utils/vault-storage';
import { registerVaultThunk } from './register-vault-thunk';

/**
 * Thunk to enable or disable sync for a vault
 * 
 * When enabling sync:
 * - If the vault doesn't exist on the server, it will be registered first
 * - Registration assigns a syncId (server vaultId) stored separately from client vaultId
 * 
 * When disabling sync:
 * - Simply sets vault.[id].sync = "false" in localStorage
 * 
 * The client vaultId never changes - syncId is used for server API calls
 */
export const setVaultSyncEnabledThunk = createAsyncThunk<
  { vaultId: string; isSyncEnabled: boolean },
  { vaultId: string; isSyncEnabled: boolean; isOnServer?: boolean },
  { state: RootState }
>(
  'vault/setVaultSyncEnabled',
  async ({ vaultId, isSyncEnabled, isOnServer = false }, { dispatch }) => {
    console.log(`[Set Sync Enabled Thunk] Setting sync for vault ${vaultId} to ${isSyncEnabled}`);
    
    if (isSyncEnabled && !isOnServer) {
      // Vault needs to be registered with the server first
      console.log(`[Set Sync Enabled Thunk] Vault ${vaultId} not on server, registering...`);
      const result = await dispatch(registerVaultThunk({ vaultId })).unwrap();
      console.log(`[Set Sync Enabled Thunk] Vault registered with syncId: ${result.syncId}`);
      // Return the same vaultId - syncId is stored separately
      return { vaultId, isSyncEnabled: true };
    }
    
    // Vault is already on server or we're disabling sync
    setVaultSyncEnabled(vaultId, isSyncEnabled);
    console.log(`[Set Sync Enabled Thunk] Sync ${isSyncEnabled ? 'enabled' : 'disabled'} for vault ${vaultId}`);
    
    return { vaultId, isSyncEnabled };
  }
);
